import {
  AfterViewInit,
  Directive,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Maskito, maskitoUpdateElement } from '@maskito/core';
import { maskitoParseNumber, maskitoStringifyNumber } from '@maskito/kit';

import {
  brightsideCurrencyMaskitoOptions,
  brightsideCurrencyNumberParams,
  type BrightsideCurrencyMaskOptions,
} from './brightside-currency-mask';

/**
 * Currency mask + {@link ControlValueAccessor} for reactive forms, replacing ngx-currency.
 * Selector and `options` input mirror ngx-currency; the form control stores `number | null`.
 */
@Directive({
  selector: 'input[currencyMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BrightsideCurrencyMaskDirective),
      multi: true,
    },
  ],
  host: {
    '(blur)': 'handleBlur()',
  },
})
export class BrightsideCurrencyMaskDirective implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private readonly _elementRef = inject(ElementRef<HTMLInputElement>);

  private _maskito: Maskito | null = null;
  private _merged: BrightsideCurrencyMaskOptions = {};
  private _numberParams = brightsideCurrencyNumberParams();
  private _nullable = true;
  private _pendingValue: number | null | undefined;
  private _onChange: (value: number | null) => void = () => undefined;
  private _onTouched: () => void = () => undefined;
  private _inputListener = (): void => this._handleInput();

  @Input() set currencyMask(value: BrightsideCurrencyMaskOptions | string | null | undefined) {
    if (value && typeof value !== 'string') {
      this._merged = { ...this._merged, ...value };
      this._refreshMask();
    }
  }

  @Input() set options(value: BrightsideCurrencyMaskOptions | null | undefined) {
    this._merged = { ...this._merged, ...(value ?? {}) };
    this._refreshMask();
  }

  ngAfterViewInit(): void {
    const el = this._elementRef.nativeElement;
    this._nullable = this._merged.nullable !== false;
    this._numberParams = brightsideCurrencyNumberParams(this._merged);
    const maskOpts = brightsideCurrencyMaskitoOptions(this._merged);
    this._maskito = new Maskito(el, maskOpts);
    el.addEventListener('input', this._inputListener);
    const align = this._merged.align;
    if (align) {
      el.style.textAlign = align;
    }
    if (this._pendingValue !== undefined) {
      this._applyWriteValue(this._pendingValue);
      this._pendingValue = undefined;
    }
  }

  ngOnDestroy(): void {
    this._elementRef.nativeElement.removeEventListener('input', this._inputListener);
    this._maskito?.destroy();
    this._maskito = null;
  }

  writeValue(value: number | string | null | undefined): void {
    const normalized = value === '' || value === undefined ? null : value === null ? null : Number(value);
    const coerced = normalized === null || Number.isFinite(normalized) ? normalized : null;
    if (!this._maskito) {
      this._pendingValue = coerced;
      return;
    }
    this._applyWriteValue(coerced);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._elementRef.nativeElement.disabled = isDisabled;
  }

  protected handleBlur(): void {
    this._onTouched();
  }

  private _applyWriteValue(value: number | null): void {
    const el = this._elementRef.nativeElement;
    if (value === null || value === undefined) {
      maskitoUpdateElement(el, '');
      return;
    }
    const display = maskitoStringifyNumber(value, this._numberParams);
    maskitoUpdateElement(el, display);
  }

  private _handleInput(): void {
    const raw = this._elementRef.nativeElement.value;
    if (this._nullable && raw.trim() === '') {
      this._onChange(null);
      return;
    }
    const parsed = maskitoParseNumber(raw, this._numberParams);
    if (Number.isNaN(parsed)) {
      return;
    }
    this._onChange(parsed);
  }

  private _refreshMask(): void {
    if (!this._maskito) {
      return;
    }
    this._maskito.destroy();
    this._nullable = this._merged.nullable !== false;
    this._numberParams = brightsideCurrencyNumberParams(this._merged);
    const maskOpts = brightsideCurrencyMaskitoOptions(this._merged);
    this._maskito = new Maskito(this._elementRef.nativeElement, maskOpts);
    const align = this._merged.align;
    if (align) {
      this._elementRef.nativeElement.style.textAlign = align;
    }
  }
}
